import * as React from "react";

import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, InfoTooltip, Modal } from "@impero-com/ui";
import { Col, Row } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

import {
    Role,
    Presence,
    AssignableRoles,
    UpdatableUsers,
} from "api/generated/model";
import {
    allValuesAreTrue,
    isIndeterminate,
    MultiCheckboxInput,
} from "components/form/legacyFields/multiCheckboxInput";
import { userRoles } from "org/user/roles";
import { FormCreate, FormRootComponentProps } from "utils/form";

import {
    getLocalizedRoleDescription,
    getLocalizedRoleName,
} from "../roleAssignment";

import * as theme from "./theme.css";

interface AssignUsersToRolesProps extends FormRootComponentProps<FormAssignRolesData> {
    assignableRoles: AssignableRoles;
    onAssignRoles: (data: UpdatableUsers) => void;
    userRolesMap: Map<number, Set<Role>>;
    onCancel: () => void;
    processing: boolean;
    controlTestingEnabled: boolean;
    controlEntitiesEnabled: boolean;
    mcpEnabled: boolean;
}

interface FormAssignRolesData {
    roles: Array<Role>;
}

interface AssignUsersToRolesState {
    userRolesMap: Map<number, Set<Role>>;
}

class AssignUsersToRoles extends React.PureComponent<
    AssignUsersToRolesProps,
    AssignUsersToRolesState
> {
    private userRoles: Role[] = userRoles({
        controlTestingEnabled: this.props.controlTestingEnabled,
        controlEntitiesEnabled: this.props.controlEntitiesEnabled,
        mcpEnabled: this.props.mcpEnabled,
    });

    constructor(props: AssignUsersToRolesProps) {
        super(props);

        const { userRolesMap } = props;

        this.state = {
            userRolesMap: new Map(userRolesMap),
        };
    }

    render = () => {
        const { form, onCancel, processing } = this.props;
        return (
            <Modal
                variant="content"
                open
                onClose={onCancel}
                width={750}
                title={t("User_Roles_Assign")}
                footer={
                    <>
                        <Button onClick={onCancel}>{t("Cancel")}</Button>
                        <Button
                            variant="primary"
                            loading={processing}
                            onClick={this.launchAssignRoles}
                        >
                            {t("Assign")}
                        </Button>
                    </>
                }
            >
                <div>
                    {form.getFieldDecorator("roles")(
                        <Row gutter={[6, 6]}>
                            {this.userRoles.map(this.checkboxForRole)}
                        </Row>
                    )}
                </div>
            </Modal>
        );
    };

    launchAssignRoles = () => {
        const { onAssignRoles, userRolesMap } = this.props;

        const allRoles = userRoles({
            controlTestingEnabled: this.props.controlTestingEnabled,
            controlEntitiesEnabled: this.props.controlEntitiesEnabled,
            mcpEnabled: this.props.mcpEnabled,
        });

        const getValuesForRole = (
            map: Map<number, Set<Role>>,
            role: Role
        ): boolean[] => {
            const values: boolean[] = [];
            map.forEach((roles) => {
                values.push(roles.has(role));
            });
            return values;
        };

        const roles = {} as Partial<Record<Role, Presence>>;
        allRoles
            .filter((role) => this.isRoleAssignable(role))
            .forEach((role) => {
                const currentValues = getValuesForRole(
                    this.state.userRolesMap,
                    role
                );
                const originalValues = getValuesForRole(userRolesMap, role);

                if (isIndeterminate(currentValues)) {
                    return;
                }

                const currentAllTrue = allValuesAreTrue(currentValues);
                const originalAllTrue = allValuesAreTrue(originalValues);
                const originalIndeterminate = isIndeterminate(originalValues);

                if (
                    currentAllTrue !== originalAllTrue ||
                    originalIndeterminate
                ) {
                    roles[role] = currentAllTrue ? "Present" : "Absent";
                }
            });

        const userIds: number[] = [];
        userRolesMap.forEach((_roles, userId) => {
            userIds.push(userId);
        });

        onAssignRoles({
            userIds,
            roles,
        });
    };

    isRoleAssignable = (role: Role): boolean => {
        const { assignableRoles } = this.props;
        switch (assignableRoles.type) {
            case "Allowed":
                return assignableRoles.roles.findIndex((r) => role === r) >= 0;
            case "Forbidden":
                return false;
            default:
                assertNever(assignableRoles);
        }
    };

    checkboxForRole = (role: Role) => {
        const { userRolesMap } = this.state;

        const disabled = !this.isRoleAssignable(role);

        const values: Array<boolean | undefined | null> = [];
        userRolesMap.forEach((roles, _userId) => {
            values.push(roles.has(role));
        });
        return (
            <Col key={"role-" + role} sm={12} span={24}>
                <div className={theme.roleContainer}>
                    <Col span={22}>
                        <MultiCheckboxInput
                            id={"role-" + role}
                            type="EDIT"
                            label={getLocalizedRoleName(role)}
                            values={values}
                            onCheckAllChange={(event: CheckboxChangeEvent) => {
                                const userRolesMap = new Map();
                                this.state.userRolesMap.forEach(
                                    (roles, userId) => {
                                        const newRoles = new Set(roles);
                                        if (event.target.checked) {
                                            newRoles.add(role);
                                        } else {
                                            newRoles.delete(role);
                                        }
                                        userRolesMap.set(userId, newRoles);
                                    }
                                );
                                this.setState({ userRolesMap });
                            }}
                            onChange={() => {}}
                            value={values}
                            disabled={disabled}
                        />
                    </Col>
                    <Col span={2} className={theme.roleIcon}>
                        <InfoTooltip text={getLocalizedRoleDescription(role)} />
                    </Col>
                </div>
            </Col>
        );
    };
}

export default FormCreate<FormAssignRolesData, AssignUsersToRolesProps>()(
    withI18n<AssignUsersToRolesProps>(AssignUsersToRoles)
);
